import { Link } from "react-router-dom";
import { Building2, Sparkles, Network, Stethoscope, ArrowUpRight } from "lucide-react";
import { SECTORS_TESTS } from "@/constants/testIds";
import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";

const SECTORS = [
  {
    key: "clinicas-esteticas",
    icon: Building2,
    title: "Clínicas Estéticas",
    image: "https://images.pexels.com/photos/16571735/pexels-photo-16571735.jpeg",
    summary: "Equipos médicos certificados CE IIb con trazabilidad clínica, protocolos por paciente y software de historial digital.",
    bullets: [
      "Cobertura dermo facial para todas las tipologías cutáneas.",
      "Trazabilidad por sesión y por paciente.",
      "Integración con software de gestión clínica.",
      "Soporte médico, formación dermatológica y respaldo legal.",
    ],
    recommended: ["axis-med", "nova-triwave"],
  },
  {
    key: "centros-belleza",
    icon: Sparkles,
    title: "Centros de Belleza",
    image: "https://images.pexels.com/photos/3736521/pexels-photo-3736521.jpeg",
    summary: "Plataformas dermo faciales optimizadas para alto rendimiento operativo, protocolos guiados y rentabilidad rápida.",
    bullets: [
      "ROI medio 6-10 meses según volumen.",
      "Interfaz táctil para operadoras junior.",
      "Sesiones express con resultados visibles.",
      "Bonos y fidelización con Préime DermaFacial.",
    ],
    recommended: ["vega-pro", "preime-dermafacial"],
  },
  {
    key: "franquicias",
    icon: Network,
    title: "Franquicias",
    image: "https://images.pexels.com/photos/8442034/pexels-photo-8442034.jpeg",
    summary: "Estandarización multi-sede de protocolos dermo faciales, formación centralizada y mantenimiento predictivo gestionado en la nube.",
    bullets: [
      "Protocolos clonables entre sedes.",
      "Onboarding remoto y certificación de personal.",
      "Dashboards de rendimiento por centro.",
      "Plan de financiación corporativa.",
    ],
    recommended: ["vega-pro", "nova-triwave"],
  },
  {
    key: "medicina-estetica",
    icon: Stethoscope,
    title: "Medicina Estética",
    image: "https://images.pexels.com/photos/9665188/pexels-photo-9665188.jpeg",
    summary: "Plataformas dermo faciales para dermatólogos, MED-spas y unidades hospitalarias que exigen equipamiento médico con software clínico.",
    bullets: [
      "Radiofrecuencia fraccional + microneedling para indicaciones médicas.",
      "Tratamiento de cicatrices, estrías y flacidez avanzada.",
      "Consola con expediente clínico digital.",
      "Compatible con dermatoscopia y derivaciones.",
    ],
    recommended: ["axis-med"],
  },
];

export default function Sectors() {
  return (
    <div className="pb-24">
      <SEO
        title="Equipos Dermo Faciales por Sector | Clínicas, Centros, Franquicias y Medicina Estética"
        description="Soluciones Asirox de equipos dermo faciales adaptadas por tipología: clínicas médico-estéticas, centros de belleza, franquicias multi-sede y medicina estética avanzada. Modelo recomendado por sector y análisis de rentabilidad."
        keywords="equipos dermo faciales clínicas estéticas, aparatología dermo facial centros belleza, plataformas dermo faciales franquicias, equipos dermo faciales medicina estética, ROI clínica estética, equipamiento profesional centros estéticos, Asirox sectores"
      />
      <PageHero
        overline="Sectores"
        title="Soluciones por tipología"
        highlight="de negocio estético."
        subtitle="Cada modelo Asirox ha sido diseñado pensando en un cliente concreto: clínica médica, centro de belleza, franquicia o unidad de medicina estética. Elige el sector y descubre la combinación óptima."
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mt-20 space-y-10">
          {SECTORS.map((s, idx) => (
            <article
              key={s.key}
              data-testid={SECTORS_TESTS.card(s.key)}
              className="grid grid-cols-1 md:grid-cols-12 gap-8 border-t border-black/15 pt-10"
            >
              <div className={`md:col-span-5 relative aspect-[4/3] overflow-hidden border border-black/15 bg-black ${idx % 2 === 0 ? "" : "md:order-2"}`}>
                <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover opacity-90"/>
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60"/>
                <p className="absolute bottom-5 left-5 overline overline-on-dark">{`0${idx+1} · Sector`}</p>
              </div>
              <div className="md:col-span-7">
                <div className="flex items-center gap-3">
                  <s.icon size={22} strokeWidth={1.6} className="text-[#3D1F0A]"/>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter ink-heading">{s.title}</h2>
                </div>
                <p className="text-black mt-5 leading-relaxed font-medium">{s.summary}</p>
                <ul className="mt-6 space-y-3">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex gap-3 text-black/85 text-sm leading-relaxed">
                      <span className="text-[#3D1F0A] font-bold">▸</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-wrap gap-3">
                  {s.recommended.map((slug) => (
                    <Link key={slug} to={`/equipos-dermo-faciales/${slug}`} className="text-xs font-mono uppercase tracking-wider px-4 py-2 border border-[#ff9c00]/60 text-[#3D1F0A] hover:border-[#ff9c00] hover:bg-[#ff9c00]/10 transition font-bold">
                      Recomendado: {slug.replace("-", " ")}
                    </Link>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-24 border border-black/15 bg-[#f1e3cb] p-10 text-center">
          <p className="overline">¿No estás seguro de tu sector?</p>
          <h3 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-3 ink-heading">Pregúntale a nuestro asistente IA.</h3>
          <Link to="/asesor-dermo-facial-ia" className="btn-primary mt-8 inline-flex">
            Hablar con Asirox AI <ArrowUpRight size={18}/>
          </Link>
        </div>
      </div>
    </div>
  );
}
